/**
 * Vincent B596.d.suite (Eric 2026-07-30 SPEC STRICK) — Page « Préférences »
 * (Menu → avatar → bouton Préférences).
 *
 * Centralise les toggles UX perso de l'utilisateur, tous persistés via
 * PATCH /api/auth/me/preferences (bag JSON libre). Suivant la philosophie
 * KNORIA : muet/défauts sains — l'utilisateur active ce qu'il souhaite.
 *
 * Toggles actifs (persistés + effet visible immédiat) :
 *   • messaging_sound_enabled : ding audio à chaque nouveau message
 *     (déjà branché en AgoraPage / ChannelsPanel via useSmartScrollAndNewMessages).
 *   • lang : langue par défaut de l'UI (fr | en). Miroir de LanguageContext.
 *
 * Toggles persistés (SANS effet visuel actuellement — préparation pour
 * les prochaines itérations) :
 *   • ui_density : compact | comfortable.
 *   • ui_theme : auto | light | dark.
 *   Sauvegardés dans le bag `preferences` côté serveur ; les composants
 *   pourront s'y brancher progressivement (ThemeProvider ETC.).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SpeakerHigh, SpeakerSlash, Translate, GridFour, Moon, SpinnerGap, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { API_BASE as API } from "../lib/apiBase";
import { playDing, setSoundEnabled as syncSoundLocal } from "../lib/messagingSound";

const SERVER_KEY_SOUND = "messaging_sound_enabled";
const SERVER_KEY_LANG = "lang";
const SERVER_KEY_DENSITY = "ui_density";
const SERVER_KEY_THEME = "ui_theme";
// Vincent P3 (Eric 2026-07-31 SPEC STRICK PROD) — Toggles email backlog.
// Le cron backend `email_digest` (à implémenter séparément) lira ces clés
// depuis `user.preferences` pour décider s'il envoie ou pas.
const SERVER_KEY_EMAIL_DIGEST_DAILY = "email_digest_daily";
const SERVER_KEY_EMAIL_DIGEST_WEEKLY = "email_digest_weekly";
const SERVER_KEY_EMAIL_FAVORITES = "email_favorites_updates";
const SERVER_KEY_EMAIL_INACTIVITY = "email_inactivity_alerts";

export default function PreferencesPage() {
  const { user, refreshUser } = useAuth();
  const { lang, switchLang } = useLang();
  // Vincent Hook Deps (Eric 2026-08-05 SPEC STRICK PROD, code review) —
  // `prefs` était une expression logique `user?.preferences || {}` évaluée à
  // chaque render → nouvelle référence à chaque re-render → le useEffect
  // ci-dessous (L58) qui a `prefs` en dep aurait re-fired en boucle.
  // `useMemo` fige la référence tant que `user?.preferences` (source
  // stable) ne change pas.
  const prefs = useMemo(() => user?.preferences || {}, [user?.preferences]);

  const [saving, setSaving] = useState(null); // clé en cours de sauvegarde
  const [soundOn, setSoundOn] = useState(Boolean(prefs[SERVER_KEY_SOUND]));
  const [density, setDensity] = useState(prefs[SERVER_KEY_DENSITY] || "comfortable");
  const [theme, setTheme] = useState(prefs[SERVER_KEY_THEME] || "auto");
  // Vincent P3 (Eric 2026-07-31 SPEC STRICK PROD) — 4 toggles email.
  const [emailDaily, setEmailDaily] = useState(Boolean(prefs[SERVER_KEY_EMAIL_DIGEST_DAILY]));
  const [emailWeekly, setEmailWeekly] = useState(Boolean(prefs[SERVER_KEY_EMAIL_DIGEST_WEEKLY]));
  const [emailFavorites, setEmailFavorites] = useState(Boolean(prefs[SERVER_KEY_EMAIL_FAVORITES]));
  const [emailInactivity, setEmailInactivity] = useState(Boolean(prefs[SERVER_KEY_EMAIL_INACTIVITY]));

  // Re-hydrate quand user.preferences change (ex. login/switch org).
  useEffect(() => {
    setSoundOn(Boolean(prefs[SERVER_KEY_SOUND]));
    setDensity(prefs[SERVER_KEY_DENSITY] || "comfortable");
    setTheme(prefs[SERVER_KEY_THEME] || "auto");
    setEmailDaily(Boolean(prefs[SERVER_KEY_EMAIL_DIGEST_DAILY]));
    setEmailWeekly(Boolean(prefs[SERVER_KEY_EMAIL_DIGEST_WEEKLY]));
    setEmailFavorites(Boolean(prefs[SERVER_KEY_EMAIL_FAVORITES]));
    setEmailInactivity(Boolean(prefs[SERVER_KEY_EMAIL_INACTIVITY]));
  }, [prefs]);

  const savePref = async (key, value) => {
    setSaving(key);
    try {
      await axios.patch(
        `${API}/api/auth/me/preferences`,
        { [key]: value },
        { withCredentials: true }
      );
      if (typeof refreshUser === "function") await refreshUser();
      toast.success(lang === "fr" ? "Préférence enregistrée" : "Preference saved");
    } catch (err) {
      toast.error(lang === "fr" ? "Erreur : préférence non enregistrée" : "Error: preference not saved");
    } finally {
      setSaving(null);
    }
  };

  const onSoundToggle = () => {
    const next = !soundOn;
    setSoundOn(next);
    syncSoundLocal(next); // met à jour localStorage + PATCH (idempotent)
    if (next) playDing(); // preview
  };

  const onLangChange = (nextLang) => {
    switchLang(nextLang); // met à jour localStorage + contexte
    savePref(SERVER_KEY_LANG, nextLang);
  };

  const onDensityChange = (nextDensity) => {
    setDensity(nextDensity);
    savePref(SERVER_KEY_DENSITY, nextDensity);
  };

  const onThemeChange = (nextTheme) => {
    setTheme(nextTheme);
    savePref(SERVER_KEY_THEME, nextTheme);
  };

  // Vincent P3 (Eric 2026-07-31 SPEC STRICK PROD) — Handler générique pour
  // les 4 toggles email. Optimistic-update + rollback local si le PATCH échoue.
  const makeEmailToggleHandler = (key, currentValue, setter) => async () => {
    const next = !currentValue;
    setter(next);  // optimistic
    setSaving(key);
    try {
      await axios.patch(
        `${API}/api/auth/me/preferences`,
        { [key]: next },
        { withCredentials: true }
      );
      if (typeof refreshUser === "function") await refreshUser();
      toast.success(lang === "fr" ? "Préférence enregistrée" : "Preference saved");
    } catch {
      setter(!next);  // rollback
      toast.error(lang === "fr" ? "Erreur : préférence non enregistrée" : "Error: preference not saved");
    } finally {
      setSaving(null);
    }
  };

  const emailToggles = [
    {
      key: SERVER_KEY_EMAIL_DIGEST_DAILY,
      value: emailDaily,
      setter: setEmailDaily,
      titleFr: "Digest quotidien",
      titleEn: "Daily digest",
      descFr: "Un résumé chaque matin (8 h HE) des nouveaux contenus, Q&R, procédures et scripts ajoutés à votre organisation.",
      descEn: "A morning summary (8 AM ET) of new content, Q&R, procedures, and scripts added to your organization.",
      testId: "pref-email-daily",
    },
    {
      key: SERVER_KEY_EMAIL_DIGEST_WEEKLY,
      value: emailWeekly,
      setter: setEmailWeekly,
      titleFr: "Digest hebdomadaire",
      titleEn: "Weekly digest",
      descFr: "Récapitulatif chaque lundi (9 h HE) : parcours complétés, top vidéos vues, tendances de l'équipe.",
      descEn: "Every Monday (9 AM ET): completed pathways, top-watched videos, team trends.",
      testId: "pref-email-weekly",
    },
    {
      key: SERVER_KEY_EMAIL_FAVORITES,
      value: emailFavorites,
      setter: setEmailFavorites,
      titleFr: "Mises à jour de vos favoris",
      titleEn: "Updates on your favorites",
      descFr: "Alerte immédiate quand une vidéo, un script ou une procédure marqué comme favori est modifié ou re-généré.",
      descEn: "Immediate alert when a video, script, or procedure you favorited is edited or re-generated.",
      testId: "pref-email-favorites",
    },
    {
      key: SERVER_KEY_EMAIL_INACTIVITY,
      value: emailInactivity,
      setter: setEmailInactivity,
      titleFr: "Alertes d'inactivité",
      titleEn: "Inactivity alerts",
      descFr: "Rappel amical après 14 jours sans connexion, pour vous aider à maintenir votre progression en formation continue AMF.",
      descEn: "Friendly reminder after 14 days without login to help maintain your AMF continuing-education progress.",
      testId: "pref-email-inactivity",
    },
  ];

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="preferences-page">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900">
          {lang === "fr" ? "Préférences" : "Preferences"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {lang === "fr"
            ? "Réglages perso synchronisés entre vos appareils."
            : "Personal settings synced across your devices."}
        </p>
      </div>

      {/* Son messagerie */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="pref-section-sound">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {soundOn ? (
              <SpeakerHigh size={22} className="text-[#002FA7] mt-0.5" />
            ) : (
              <SpeakerSlash size={22} className="text-zinc-400 mt-0.5" />
            )}
            <div>
              <h3 className="font-medium text-zinc-900">
                {lang === "fr" ? "Son des nouveaux messages" : "New message sound"}
              </h3>
              <p className="text-sm text-zinc-500 mt-0.5">
                {lang === "fr"
                  ? "Joue un ding discret (10 % volume, 150 ms) quand un nouveau message arrive pendant que vous lisez l'historique."
                  : "Plays a discrete ding (10 % volume, 150 ms) when a new message arrives while you're reading history."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onSoundToggle}
            role="switch"
            aria-checked={soundOn}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${soundOn ? "bg-[#002FA7]" : "bg-zinc-300"}`}
            data-testid="pref-sound-toggle"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${soundOn ? "translate-x-5" : "translate-x-0.5"}`}
            />
          </button>
        </div>
      </section>

      {/* Langue */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="pref-section-lang">
        <div className="flex items-start gap-3 mb-3">
          <Translate size={22} className="text-[#002FA7] mt-0.5" />
          <div>
            <h3 className="font-medium text-zinc-900">
              {lang === "fr" ? "Langue de l'interface" : "Interface language"}
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Change immédiatement la langue sur cet appareil et pour vos prochaines connexions."
                : "Immediately changes the language on this device and for your next sign-ins."}
            </p>
          </div>
        </div>
        <div className="flex gap-2 ml-8">
          {["fr", "en"].map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => onLangChange(code)}
              disabled={saving === SERVER_KEY_LANG}
              className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${lang === code ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
              data-testid={`pref-lang-${code}`}
            >
              {code === "fr" ? "Français" : "English"}
            </button>
          ))}
          {saving === SERVER_KEY_LANG && <SpinnerGap size={16} className="animate-spin text-zinc-400 self-center" />}
        </div>
      </section>

      {/* Densité (soft launch — persisté sans effet visuel) */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="pref-section-density">
        <div className="flex items-start gap-3 mb-3">
          <GridFour size={22} className="text-[#002FA7] mt-0.5" />
          <div>
            <h3 className="font-medium text-zinc-900 flex items-center gap-2">
              {lang === "fr" ? "Densité d'affichage" : "Display density"}
              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 uppercase">
                {lang === "fr" ? "Bientôt" : "Coming soon"}
              </span>
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Préférence enregistrée dès maintenant. Application progressive dans les prochaines mises à jour."
                : "Preference saved now. Progressive rollout in upcoming releases."}
            </p>
          </div>
        </div>
        <div className="flex gap-2 ml-8">
          {[
            { code: "comfortable", labelFr: "Confort", labelEn: "Comfortable" },
            { code: "compact", labelFr: "Compact", labelEn: "Compact" },
          ].map(({ code, labelFr, labelEn }) => (
            <button
              key={code}
              type="button"
              onClick={() => onDensityChange(code)}
              disabled={saving === SERVER_KEY_DENSITY}
              className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${density === code ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
              data-testid={`pref-density-${code}`}
            >
              {lang === "fr" ? labelFr : labelEn}
            </button>
          ))}
          {saving === SERVER_KEY_DENSITY && <SpinnerGap size={16} className="animate-spin text-zinc-400 self-center" />}
        </div>
      </section>

      {/* Thème (soft launch — persisté sans effet visuel) */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="pref-section-theme">
        <div className="flex items-start gap-3 mb-3">
          <Moon size={22} className="text-[#002FA7] mt-0.5" />
          <div>
            <h3 className="font-medium text-zinc-900 flex items-center gap-2">
              {lang === "fr" ? "Thème" : "Theme"}
              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 uppercase">
                {lang === "fr" ? "Bientôt" : "Coming soon"}
              </span>
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Préférence enregistrée dès maintenant. Application progressive dans les prochaines mises à jour."
                : "Preference saved now. Progressive rollout in upcoming releases."}
            </p>
          </div>
        </div>
        <div className="flex gap-2 ml-8">
          {[
            { code: "auto", labelFr: "Auto (système)", labelEn: "Auto (system)" },
            { code: "light", labelFr: "Clair", labelEn: "Light" },
            { code: "dark", labelFr: "Sombre", labelEn: "Dark" },
          ].map(({ code, labelFr, labelEn }) => (
            <button
              key={code}
              type="button"
              onClick={() => onThemeChange(code)}
              disabled={saving === SERVER_KEY_THEME}
              className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${theme === code ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
              data-testid={`pref-theme-${code}`}
            >
              {lang === "fr" ? labelFr : labelEn}
            </button>
          ))}
          {saving === SERVER_KEY_THEME && <SpinnerGap size={16} className="animate-spin text-zinc-400 self-center" />}
        </div>
      </section>

      {/* Vincent P3 (Eric 2026-07-31 SPEC STRICK PROD) — Notifications par email.
          Les 4 toggles sont persistés dans user.preferences ; le cron
          `email_digest` (à implémenter côté backend) les lira pour décider
          quand envoyer. Le libellé « Bientôt » indique que la préférence est
          bien enregistrée dès maintenant mais que l'envoi effectif arrive
          dans une prochaine mise à jour. */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="pref-section-email">
        <div className="flex items-start gap-3 mb-4">
          <EnvelopeSimple size={22} className="text-[#002FA7] mt-0.5" />
          <div>
            <h3 className="font-medium text-zinc-900 flex items-center gap-2">
              {lang === "fr" ? "Notifications par email" : "Email notifications"}
              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 uppercase">
                {lang === "fr" ? "Envoi bientôt" : "Delivery coming soon"}
              </span>
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Choisissez les alertes email que vous souhaitez recevoir. Aucun email n'est envoyé sans votre activation explicite ; vos préférences sont enregistrées dès maintenant."
                : "Choose the email alerts you want to receive. No email is sent without your explicit opt-in; your preferences are saved right away."}
            </p>
          </div>
        </div>
        <div className="space-y-3 ml-8">
          {emailToggles.map(({ key, value, setter, titleFr, titleEn, descFr, descEn, testId }) => (
            <div key={key} className="flex items-start justify-between gap-4 py-2 border-t border-zinc-100 first:border-t-0">
              <div className="flex-1">
                <div className="text-sm font-medium text-zinc-800">
                  {lang === "fr" ? titleFr : titleEn}
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {lang === "fr" ? descFr : descEn}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {saving === key && <SpinnerGap size={14} className="animate-spin text-zinc-400" />}
                <button
                  type="button"
                  onClick={makeEmailToggleHandler(key, value, setter)}
                  disabled={saving === key}
                  role="switch"
                  aria-checked={value}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${value ? "bg-[#002FA7]" : "bg-zinc-300"}`}
                  data-testid={testId}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
